import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'

import { useApiAxios } from '@lib/rest-web'
import { useDebounce } from '@lib/web'
import { DataRecords, Page, PageRequest } from '@piv/lib/api-types'

export type { DataRecords as Types }

export const useDataRecords = (
  dataPackageId: string,
  subAssetId: string,
  filters: DataRecords.DataRecordFilters
) => {
  const api = useApiAxios()

  return useInfiniteQuery<Page<DataRecords.DataRecord>>({
    queryKey: ['data-records', dataPackageId, subAssetId, api, filters],
    queryFn: async (context) => {
      const nextPage = (context.pageParam ?? {}) as PageRequest

      const result = await api.get<Page<DataRecords.DataRecord>>(
        `data-records/by-package/${dataPackageId}/records`,
        {
          params: {
            ...nextPage,
            limit: 25,
            ...filters,
            subAssetId: subAssetId,
          } as DataRecords.QueryParams,
          signal: context.signal,
          validateStatus: (status) => status === 200,
        }
      )

      return result.data
    },

    initialPageParam: {},
    getNextPageParam: (page) => page.next ?? undefined,
    placeholderData: keepPreviousData,
  })
}

export const useRecord = (
  dataPackageId: string,
  recordName: string | false
) => {
  const api = useApiAxios()

  return useQuery({
    queryKey: ['record', api, dataPackageId, recordName],
    queryFn: async () => {
      if (recordName === false || !dataPackageId) {
        return null
      }

      const result = await api.get<DataRecords.DataRecordDetail>(
        `data-records/by-package/${dataPackageId}/records/${recordName}/detail`,
        {
          validateStatus: (status) => status === 200,
        }
      )

      return result.data
    },
  })
}

export const useSearchedDataRecords = (query: string) => {
  const api = useApiAxios()
  const debouncedQuery = useDebounce(query, 500)

  return useInfiniteQuery<Page<DataRecords.SearchResultDataRecord>>({
    queryKey: ['data-records', debouncedQuery],
    queryFn: async (context) => {
      const nextPage = (context.pageParam ?? {}) as PageRequest

      const result = await api.get<Page<DataRecords.SearchResultDataRecord>>(
        `data-records`,
        {
          params: {
            q: debouncedQuery,
            limit: nextPage.limit ?? 20,
            offset: nextPage.offset ?? 0,
          } as DataRecords.SearchQueryParams,
          signal: context.signal,
          validateStatus: (status) => status === 200,
        }
      )

      return result.data
    },

    initialPageParam: {},
    getNextPageParam: (page) => page.next ?? undefined,
    placeholderData: keepPreviousData,
  })
}
